import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bug, Lightbulb, MessageCircle, Filter, Trash2, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const KIND_META = {
  bug: { icon: Bug, color: "bg-rose-100 text-rose-700", label: "Bug" },
  feature: { icon: Lightbulb, color: "bg-amber-100 text-amber-700", label: "Feature" },
  general: { icon: MessageCircle, color: "bg-blue-100 text-blue-700", label: "General" },
};

const STATUS_META = {
  new: { color: "bg-zinc-100 text-zinc-700", label: "New" },
  in_review: { color: "bg-amber-100 text-amber-700", label: "In review" },
  resolved: { color: "bg-emerald-100 text-emerald-700", label: "Resolved" },
  closed: { color: "bg-zinc-200 text-zinc-500", label: "Closed" },
};

export default function SuperAdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (kindFilter !== "all") params.kind = kindFilter;
      const { data } = await api.get("/superadmin/feedback", { params });
      setItems(data);
    } catch { toast.error("Failed to load feedback"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, kindFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/superadmin/feedback/${id}`, { status });
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, status } : x));
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await api.delete(`/superadmin/feedback/${id}`);
      setItems(items.filter((x) => x.id !== id));
    } catch { toast.error("Failed"); }
  };

  if (!user?.is_super_admin) return <div className="p-8 text-zinc-500">Super-admin only.</div>;

  const counts = items.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Platform</div>
        <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
          <MessageCircle className="w-8 h-8 text-[#FF5A00]"/> Beta Feedback
        </h1>
        <p className="text-zinc-500 mt-1">Bug reports, feature requests, and general feedback from all beta businesses.</p>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="text-xs text-zinc-500 flex items-center gap-1"><Filter className="w-3 h-3"/>Filters:</div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="feedback-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_review">In review</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-32 h-8 text-xs" data-testid="feedback-filter-kind"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="bug">Bug</SelectItem>
            <SelectItem value="feature">Feature</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-zinc-500 ml-auto font-mono-accent">
          {items.length} shown · {counts.new || 0} new · {counts.in_review || 0} in review
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">No feedback yet.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {items.map((f) => {
              const K = KIND_META[f.kind] || KIND_META.general;
              const Icon = K.icon;
              return (
                <div key={f.id} className="p-5" data-testid={`feedback-item-${f.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-mono-accent uppercase ${K.color}`}>
                          <Icon className="w-3 h-3"/>{K.label}
                        </span>
                        <span className="text-xs font-medium">{f.business_name || "—"}</span>
                        <span className="text-xs text-zinc-500">·</span>
                        <span className="text-xs text-zinc-500">{f.user_name || f.user_email}</span>
                        {f.page && <>
                          <span className="text-xs text-zinc-500">·</span>
                          <span className="text-xs text-zinc-500 font-mono-accent">{f.page}</span>
                        </>}
                        <span className="text-xs text-zinc-400 ml-auto">{new Date(f.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-zinc-800 whitespace-pre-wrap break-words">{f.message}</div>
                      {f.admin_note && <div className="mt-2 text-xs text-zinc-500 italic border-l-2 border-zinc-200 pl-2">Note: {f.admin_note}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Select value={f.status} onValueChange={(v)=>updateStatus(f.id, v)}>
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`feedback-status-${f.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in_review">In review</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] uppercase tracking-widest font-mono-accent px-2 py-0.5 rounded ${STATUS_META[f.status]?.color}`}>{STATUS_META[f.status]?.label}</span>
                        <button onClick={()=>remove(f.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`feedback-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
